import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  Sheet,
  Input,
  Select,
  Option,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Switch,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Autocomplete,
} from '@mui/joy';
import {
  Plus as Add,
  PencilSimple as Edit,
  Trash as Delete,
  Play as PlayArrow,
  Ruler as Rule,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { 
  BankRule,
  ChartOfAccount,
  BankRuleMatchType 
} from '../../../types/accounting';

export const BankRules: React.FC = () => {
  const [rules, setRules] = useState<BankRule[]>([]);
  const [chartAccounts, setChartAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<BankRule | null>(null);
  const [formData, setFormData] = useState<Partial<BankRule>>({
    name: '',
    description: '',
    match_type: 'CONTAINS',
    match_text: '',
    match_merchant: '',
    match_amount_min: undefined,
    match_amount_max: undefined,
    category_account: 0,
    auto_create_journal: false,
    is_active: true,
    priority: 0,
    apply_to_existing: false,
  });

  useEffect(() => {
    fetchRules();
    fetchChartAccounts();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiAccountingClient.getBankRules();
      setRules(response.results);
    } catch (err) {
      console.error('Failed to fetch bank rules:', err);
      setError('Failed to load bank rules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartAccounts = async () => {
    try {
      const response = await apiAccountingClient.getChartOfAccounts({ is_active: true });
      setChartAccounts(response.results);
    } catch (err) {
      console.error('Failed to fetch chart of accounts:', err);
    }
  };

  const handleOpenForm = (rule?: BankRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        name: rule.name,
        description: rule.description,
        match_type: rule.match_type,
        match_text: rule.match_text,
        match_merchant: rule.match_merchant,
        match_amount_min: rule.match_amount_min,
        match_amount_max: rule.match_amount_max,
        category_account: rule.category_account,
        auto_create_journal: rule.auto_create_journal,
        is_active: rule.is_active,
        priority: rule.priority,
        apply_to_existing: rule.apply_to_existing,
      });
    } else {
      setEditingRule(null);
      setFormData({
        name: '',
        description: '',
        match_type: 'CONTAINS',
        match_text: '',
        match_merchant: '',
        match_amount_min: undefined,
        match_amount_max: undefined,
        category_account: 0,
        auto_create_journal: false,
        is_active: true,
        priority: 0,
        apply_to_existing: false,
      });
    }
    setFormModalOpen(true);
  };

  const handleSaveRule = async () => {
    try {
      if (!formData.name || !formData.match_text || !formData.category_account) {
        alert('Please fill in all required fields');
        return;
      }

      if (editingRule) {
        await apiAccountingClient.updateBankRule(editingRule.id, formData);
      } else {
        await apiAccountingClient.createBankRule(formData as any);
      }

      setFormModalOpen(false);
      await fetchRules();
    } catch (err) {
      console.error('Failed to save rule:', err);
      alert('Failed to save rule. Please try again.');
    }
  };

  const handleDeleteRule = async (id: number) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    try {
      await apiAccountingClient.deleteBankRule(id);
      await fetchRules();
    } catch (err) {
      console.error('Failed to delete rule:', err);
      alert('Failed to delete rule. Please try again.');
    }
  };

  const handleApplyRule = async (id: number) => {
    try {
      const result = await apiAccountingClient.applyBankRule(id);
      alert(`Rule applied successfully! Matched ${result.transactions_matched} transactions.`);
      await fetchRules();
    } catch (err) {
      console.error('Failed to apply rule:', err);
      alert('Failed to apply rule. Please try again.');
    }
  };

  const handleToggleActive = async (rule: BankRule) => {
    try {
      await apiAccountingClient.updateBankRule(rule.id, {
        is_active: !rule.is_active,
      });
      await fetchRules();
    } catch (err) {
      console.error('Failed to update rule:', err);
      alert('Failed to update rule. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
        <div>
          <Typography level="h2" sx={{ mb: 1, fontSize: { xs: '1.5rem', md: undefined } }}>
            Categorization Rules
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Create rules to automatically categorize bank transactions
          </Typography>
        </div>
        <Button startDecorator={<Add />} onClick={() => handleOpenForm()}>
          New Rule
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {rules.length === 0 ? (
        <Sheet variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 'sm' }}>
          <Rule size={48} color="var(--joy-palette-text-tertiary)" style={{ marginBottom: 16 }} />
          <Typography level="h4" sx={{ mb: 1 }}>
            No categorization rules yet
          </Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary', mb: 3 }}>
            Create rules to automatically categorize incoming bank transactions
          </Typography>
          <Button startDecorator={<Add />} onClick={() => handleOpenForm()}>
            Create Your First Rule
          </Button>
        </Sheet>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th>Active</th>
                <th>Rule Name</th>
                <th>Match Type</th>
                <th>Match Text</th>
                <th>Category</th>
                <th>Auto Journal</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <Switch
                      checked={rule.is_active}
                      onChange={() => handleToggleActive(rule)}
                    />
                  </td>
                  <td>
                    <Box>
                      <Typography level="body-sm">{rule.name}</Typography>
                      {rule.description && (
                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                          {rule.description}
                        </Typography>
                      )}
                    </Box>
                  </td>
                  <td>
                    <Chip size="sm" variant="outlined">
                      {rule.match_type}
                    </Chip>
                  </td>
                  <td>
                    <Typography level="body-sm">{rule.match_text}</Typography>
                    {rule.match_merchant && (
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        Merchant: {rule.match_merchant}
                      </Typography>
                    )}
                  </td>
                  <td>
                    <Typography level="body-sm">{rule.category_account_name}</Typography>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      color={rule.auto_create_journal ? 'success' : 'neutral'}
                      variant="soft"
                    >
                      {rule.auto_create_journal ? 'Yes' : 'No'}
                    </Chip>
                  </td>
                  <td>{rule.priority}</td>
                  <td>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="primary"
                        onClick={() => handleApplyRule(rule.id)}
                        disabled={!rule.is_active}
                      >
                        <PlayArrow />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => handleOpenForm(rule)}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Rule Form Modal */}
      <Modal open={formModalOpen} onClose={() => setFormModalOpen(false)}>
        <ModalDialog size="lg" sx={{ maxWidth: { xs: 'calc(100vw - 16px)', sm: 'none' }, maxHeight: '90vh', overflow: 'auto' }}>
          <DialogTitle>
            {editingRule ? 'Edit Rule' : 'New Categorization Rule'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <FormControl>
                <FormLabel>Rule Name *</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Amazon Purchases"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description..."
                />
              </FormControl>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 2 }}>
                <FormControl>
                  <FormLabel>Match Type *</FormLabel>
                  <Select
                    value={formData.match_type}
                    onChange={(e, value) => setFormData({ ...formData, match_type: value as BankRuleMatchType })}
                  >
                    <Option value="CONTAINS">Contains</Option>
                    <Option value="EXACT">Exact Match</Option>
                    <Option value="STARTS_WITH">Starts With</Option>
                    <Option value="ENDS_WITH">Ends With</Option>
                    <Option value="REGEX">Regular Expression</Option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Match Text *</FormLabel>
                  <Input
                    value={formData.match_text}
                    onChange={(e) => setFormData({ ...formData, match_text: e.target.value })}
                    placeholder="Text to match in transaction description"
                  />
                </FormControl>
              </Box>

              <FormControl>
                <FormLabel>Merchant Name (Optional)</FormLabel>
                <Input
                  value={formData.match_merchant}
                  onChange={(e) => setFormData({ ...formData, match_merchant: e.target.value })}
                  placeholder="Optional merchant name to match"
                />
              </FormControl>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl>
                  <FormLabel>Min Amount (Optional)</FormLabel>
                  <Input
                    type="number"
                    value={formData.match_amount_min || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      match_amount_min: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                    placeholder="0.00"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Max Amount (Optional)</FormLabel>
                  <Input
                    type="number"
                    value={formData.match_amount_max || ''}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      match_amount_max: e.target.value ? parseFloat(e.target.value) : undefined 
                    })}
                    placeholder="0.00"
                  />
                </FormControl>
              </Box>

              <FormControl>
                <FormLabel>Category Account *</FormLabel>
                <Autocomplete
                  options={chartAccounts}
                  getOptionLabel={(option) => `${option.account_number} - ${option.name}`}
                  value={chartAccounts.find(a => a.id === formData.category_account) || null}
                  onChange={(e, value) => setFormData({ ...formData, category_account: value?.id || 0 })}
                  placeholder="Select category account..."
                />
              </FormControl>

              <FormControl>
                <FormLabel>Priority</FormLabel>
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    priority: parseInt(e.target.value) || 0 
                  })}
                  placeholder="0"
                  slotProps={{
                    input: { min: 0 }
                  }}
                />
              </FormControl>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch
                    checked={formData.auto_create_journal}
                    onChange={(e) => setFormData({ ...formData, auto_create_journal: e.target.checked })}
                  />
                  <Typography level="body-sm">
                    Automatically create journal entries
                  </Typography>
                </Box>

                {!editingRule && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={formData.apply_to_existing}
                      onChange={(e) => setFormData({ ...formData, apply_to_existing: e.target.checked })}
                    />
                    <Typography level="body-sm">
                      Apply to existing uncategorized transactions
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSaveRule}>
              {editingRule ? 'Save Changes' : 'Create Rule'}
            </Button>
            <Button variant="plain" onClick={() => setFormModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};